import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import IconPicker from '../../../shared/buttonEditor/IconPicker';
import UploadedImageField from '../../../shared/buttonEditor/UploadedImageField';
import { isSafeHttpUrl } from '../../../services/shortcuts/shortcutUrl';
import { SCOPE_SHARED } from '../../../services/shortcuts/shortcutModel';
import { uploadLauncherIcon } from '../../../services/homeLauncher/uploadLauncherIcon';
import { EDITOR_DIALOG_Z_INDEX, EDITOR_MENU_PROPS } from '../../../shared/buttonEditor/editorLayers';

export default function ShortcutEditorDialog({
  open,
  shortcut,
  tenantId = '',
  imageScope = 'personal',
  onClose,
  onSave,
  onDelete,
}) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [iconType, setIconType] = useState('catalog');
  const [iconKey, setIconKey] = useState('website');
  const [iconImageAssetId, setIconImageAssetId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel(shortcut?.label || '');
    setUrl(shortcut?.url || '');
    setIconType(
      shortcut?.iconType === 'image' || shortcut?.iconType === 'generate' ? 'image' : 'catalog'
    );
    setIconKey(shortcut?.iconKey || 'website');
    setIconImageAssetId(shortcut?.iconImageAssetId || '');
    setError('');
    setBusy(false);
    setImageBusy(false);
  }, [open, shortcut]);

  const scope = imageScope === SCOPE_SHARED ? SCOPE_SHARED : 'personal';
  const needsImage = iconType === 'image';
  const saveDisabled = busy || imageBusy || (needsImage && !iconImageAssetId);

  const handleSave = async () => {
    if (!label.trim()) {
      setError('Add a label');
      return;
    }
    if (!isSafeHttpUrl(url)) {
      setError('Use a valid http or https URL');
      return;
    }
    if (needsImage && !iconImageAssetId) {
      setError('Choose an image before saving');
      return;
    }
    setBusy(true);
    try {
      await onSave({
        id: shortcut?.id,
        label: label.trim(),
        url: url.trim(),
        iconType: needsImage ? 'image' : 'catalog',
        iconKey: needsImage ? null : iconKey,
        iconImageAssetId: needsImage ? iconImageAssetId : null,
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not save shortcut');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!shortcut?.id || !onDelete) return;
    setBusy(true);
    try {
      await onDelete(shortcut.id);
      onClose();
    } catch (err) {
      setError(err?.message || 'Could not delete shortcut');
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy || imageBusy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: EDITOR_DIALOG_Z_INDEX }}
    >
      <DialogTitle>{shortcut?.id ? 'Edit shortcut' : 'Add shortcut'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Label"
            value={label}
            onChange={e => setLabel(e.target.value)}
            autoFocus
            size="small"
          />
          <TextField
            label="URL"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://"
            size="small"
            helperText="Only HTTP and HTTPS links are allowed"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="shortcut-icon-type-label">Icon</InputLabel>
            <Select
              labelId="shortcut-icon-type-label"
              label="Icon"
              value={iconType}
              onChange={event => setIconType(event.target.value)}
              MenuProps={EDITOR_MENU_PROPS}
            >
              <MenuItem value="catalog">Catalog icon</MenuItem>
              <MenuItem value="image">Uploaded image</MenuItem>
            </Select>
          </FormControl>
          {iconType === 'catalog' ? (
            <IconPicker
              value={iconKey}
              onChange={setIconKey}
              helperText="Choose an icon for this shortcut."
            />
          ) : null}
          {iconType === 'image' ? (
            <UploadedImageField
              assetId={iconImageAssetId}
              tenantId={tenantId}
              alt={label || 'Shortcut image'}
              helperText="PNG, JPEG, or WebP. The shortcut stores the image id only."
              onUpload={file => uploadLauncherIcon({
                file,
                tenantId,
                scope,
                targetKind: 'shortcut',
              })}
              onUploaded={setIconImageAssetId}
              onBusyChange={setImageBusy}
            />
          ) : null}
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {shortcut?.id ? (
          <Button color="error" onClick={handleDelete} disabled={busy || imageBusy}>
            Delete
          </Button>
        ) : null}
        <Button onClick={onClose} disabled={busy || imageBusy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saveDisabled}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
